/**
 * Unit-price precision helper.
 *
 * Admin Setup → Regional → Decimal Precision (`unit_price_decimals`, CR-4707)
 * sets the *default* precision a price is shown at. It is not a limit and not
 * a rounding rule: a user who needs 3 decimals on a particular line can type
 * them, and a source price that carries more decimals than the default keeps
 * every one of them.
 *
 * Two things make a helper necessary rather than rendering the raw value:
 *
 * - `NonConformanceReport.unit_price` is a 4-decimal column, so the API
 *   returns the stored scale verbatim — "10.0000" for a price entered as 10.
 * - PO lines and GRNs carry 3 decimals whatever the setting says.
 *
 * @link NCRCreate for the prefill path
 * @link useNCREdit for the load path
 */

/**
 * Render a price at the configured default precision, never dropping a
 * decimal the value actually carries.
 *
 * Pads up to the default and stops there — it will not round. A price stored
 * as 10 shows as "10.00" at precision 2 instead of the raw "10.0000"; one of
 * 10.375 stays "10.375" on that same company rather than becoming "10.38".
 *
 * @param value - price from a record or an input, string or number
 * @param decimals - `unit_price_decimals` from company preferences (0–4)
 * @returns the price as a string, or '' when it isn't a number
 */
export function displayPrice(value: string | number, decimals: number): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  // String(n) drops trailing zeros, so this is the value's *real* precision.
  const significant = (String(n).split('.')[1] || '').length;
  return n.toFixed(Math.max(decimals, significant));
}
